import { ErrorScreen } from '../../../../Error';
import Button from '@/components/Button';
import { LoadingContainer } from '../../../shared/components/loading-container';
import { Suspense } from 'react';
import { Hero } from '../hero';
import { useAssessmentStore } from '../../store/useAssessmentStore';
import { ConsoleCJ } from '../../../../Error/Console';
import Container from '@/components/Container';
import PageHeader from '@/components/PageHeader';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/services/lib/utils';
import { useDoneAssessment } from '../../hooks/useDoneAssessment';
import { useNavigate } from 'react-router-dom';
import { useMissionRouteParams } from '@/pages/mission/shared/hooks/useMissionRouteParams';

const AllDoneScreenComponent = () => {
  const { title, description, mission, errorAssessment, postAssessment, preAssessment, hasReportingToolsAccess } =
    useDoneAssessment();
  const { chapterTitleEncoded } = useMissionRouteParams();
  const navigate = useNavigate();
  const assessmentPage = useAssessmentStore((state) => state.assessmentPage);
  const max = 10;
  const ticks = [...Array(max + 1)].map((_, i) => i);
  const skipInterval = 1;

  if (errorAssessment) {
    ConsoleCJ.customError('AllDoneScreen', {
      ...errorAssessment,
    });
    return <ErrorScreen />;
  }

  return (
    <Container className="!mt-sh-60">
      <div className="col-span-12 lg:col-start-3 lg:col-end-11">
        <PageHeader title={title || 'All done'} type="none" />
        <div className="mt-5 pb-10">
          <Hero title={title || ''} description={description || ''} />

          <div className="mt-4 flex items-start justify-start gap-4">
            {hasReportingToolsAccess && (
              <Button
                theme="outline"
                onClick={() => {
                  navigate('/reporting-tools');
                }}>
                {assessmentPage?.allDoneSection?.goToReportingTools}
              </Button>
            )}
            <Button
              theme="outline"
              onClick={() => {
                navigate(`/mission/${mission?.slug}/chapter/${chapterTitleEncoded}/assessment`);
              }}>
              {assessmentPage?.allDoneSection?.createNew}
            </Button>
          </div>
          <h3 className="my-2 mt-6 font-cadiz text-sh-28-32 font-bold">
            {`${assessmentPage?.cohortSection?.preAssess}`}
          </h3>
          <p className="font-cadiz text-base text-gray-600">{assessmentPage?.cohortSection?.caption}</p>
          <p className="mt-2 font-cadiz text-base font-bold">{mission?.trait?.trait}</p>

          <div className="mt-5">
            <Slider defaultValue={[preAssessment?.assessment || 0]} max={max} disabled />
            <span
              className="mt-3 flex w-full items-center justify-between gap-1 px-2.5 text-xs font-medium text-muted-foreground"
              aria-hidden="true">
              {ticks.map((_, i) => (
                <span key={i} className="flex w-0 flex-col items-center justify-center gap-2">
                  <span className={cn('h-1 w-px bg-muted-foreground/70', i % skipInterval !== 0 && 'h-0.5')} />
                  <span className={cn(i % skipInterval !== 0 && 'opacity-0')}>{i}</span>
                </span>
              ))}
            </span>
          </div>
          <h3 className="my-2 mt-10 font-cadiz text-sh-28-32 font-bold">
            {`${assessmentPage?.cohortSection?.postAssess}`}
          </h3>
          <p className="font-cadiz text-base text-gray-600">{assessmentPage?.cohortSection?.caption}</p>
          <p className="mt-2 font-cadiz text-base font-bold">{mission?.trait?.trait}</p>

          <div className="mt-5">
            <Slider defaultValue={[postAssessment?.assessment || 0]} max={max} disabled />
            <span
              className="mt-3 flex w-full items-center justify-between gap-1 px-2.5 text-xs font-medium text-muted-foreground"
              aria-hidden="true">
              {ticks.map((_, i) => (
                <span key={i} className="flex w-0 flex-col items-center justify-center gap-2">
                  <span className={cn('h-1 w-px bg-muted-foreground/70', i % skipInterval !== 0 && 'h-0.5')} />
                  <span className={cn(i % skipInterval !== 0 && 'opacity-0')}>{i}</span>
                </span>
              ))}
            </span>
          </div>
        </div>
      </div>
    </Container>
  );
};

export const AllDoneScreen = () => {
  return (
    <Suspense fallback={<LoadingContainer title="Assessment" />}>
      <AllDoneScreenComponent />
    </Suspense>
  );
};
